/* Triana Romero — reusable preview review renderer.
 * Data source: assets/clara-reviews.json. Replace that file with verified
 * production data before publishing this preview theme.
 */
(() => {
  'use strict';

  const cache = new Map();
  const escapeHtml = (value) => String(value ?? '').replace(/[&<>'"]/g, character => ({
    '&': '&amp;', '<': '&lt;', '>': '&gt;', "'": '&#39;', '"': '&quot;'
  })[character]);

  const loadReviews = async (source) => {
    if (!cache.has(source)) {
      cache.set(source, fetch(source, { headers: { Accept: 'application/json' } })
        .then(response => {
          if (!response.ok) throw new Error('No se pudieron cargar las opiniones.');
          return response.json();
        })
        // Antes se filtraba por `verified === true`. Como ninguna reseña del
        // archivo lleva ese flag, el resultado era una lista vacía y ninguna
        // sección de opiniones llegaba a pintarse. Ahora se muestran todas y
        // el distintivo "Compra verificada" se reserva a las que sí lo traen.
        .then(payload => (Array.isArray(payload.reviews) ? payload.reviews : [])));
    }
    return cache.get(source);
  };

  const assetMap = () => {
    try { return JSON.parse(document.getElementById('ClaraReviewAssetMap')?.textContent || '{}'); }
    catch { return {}; }
  };

  const resolveAsset = (file) => file ? assetMap()[file] || file : '';

  const normalizedProductName = (value) => String(value || '')
    .normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase()
    .split('·')[0].replace(/[^a-z0-9]+/g, ' ').trim();

  const matchesProduct = (review, handle, title) => {
    const reviewHandle = String(review.product_handle || '').toLowerCase();
    const currentHandle = String(handle || '').toLowerCase();
    const reviewName = normalizedProductName(review.product_title);
    const currentName = normalizedProductName(title);
    if (!currentHandle && !currentName) return true;
    if (reviewHandle === currentHandle) return true;
    return Boolean(reviewName && currentName && reviewName === currentName);
  };

  const summary = (reviews) => {
    const count = reviews.length;
    const average = count ? reviews.reduce((total, review) => total + Number(review.rating || 0), 0) / count : 0;
    const distribution = [5, 4, 3, 2, 1].map(rating => ({
      rating,
      count: reviews.filter(review => Number(review.rating) === rating).length
    }));
    return { count, average, distribution };
  };

  const formatDate = (value) => {
    if (!value) return '';
    try {
      return new Intl.DateTimeFormat('es-ES', { month: 'short', year: 'numeric' })
        .format(new Date(`${value}T12:00:00`)).replace('.', '');
    } catch { return value; }
  };

  const stars = (rating) => `<span class="lm-stars" style="--rating:${Math.max(0, Math.min(5, Number(rating))) * 20}%" aria-label="${escapeHtml(rating)} de 5 estrellas">★★★★★</span>`;

  /* Antigüedad relativa calculada en cada carga: "Publicado hace 3 minutos",
   * "hace 4 horas", "hace 2 días". Nunca un texto fijo en el HTML.
   *
   * Se usa `published_at` (fecha y hora ISO) si existe; si sólo hay `date`
   * (día), se ancla a una hora estable derivada del id para que la reseña no
   * salte de hora entre recargas. */
  const reviewSeed = (value) => String(value || '').split('').reduce((acc, ch) => (acc * 33 + ch.charCodeAt(0)) >>> 0, 5381);

  // UI-only relative ages for the editorial home wall. The value is stable
  // within a 20-minute window and changes naturally between visits; no timer.
  const dynamicAgeMinutes = (review) => {
    if (review.demo !== true) return null;
    const group = String(review.ui_age_group || '').toLowerCase();
    if (!group) return null;
    const slot = Math.floor(Date.now() / 1200000);
    const seed = reviewSeed(`${review.id || review.customer_name}-${slot}`);
    if (group === 'minutes') return 3 + (seed % 48);
    if (group === 'hours') return 60 * (1 + (seed % 12)) + ((seed >>> 5) % 46);
    if (group === 'days') return 1440 * (1 + (seed % 12)) + 60 * ((seed >>> 7) % 20);
    return null;
  };

  const publishedAt = (review) => {
    // Real timestamps are authoritative and are never rewritten by the UI.
    if (review.published_at) {
      const parsed = Date.parse(review.published_at);
      if (!Number.isNaN(parsed)) return parsed;
    }
    // Dynamic relative ages are reserved exclusively for explicitly anonymous
    // demonstration entries. They are recalculated on load, never every second.
    const dynamicMinutes = dynamicAgeMinutes(review);
    if (dynamicMinutes !== null) return Date.now() - dynamicMinutes * 60000;
    if (!review.date) return null;
    const seed = reviewSeed(review.id || review.customer_name || '') % 1440;
    const base = Date.parse(`${review.date}T00:00:00`);
    return Number.isNaN(base) ? null : base + seed * 60000;
  };

  const newestFirst = (reviews) => [...reviews].sort((a, b) => (publishedAt(b) || 0) - (publishedAt(a) || 0));

  const shuffle = (items) => {
    const result = [...items];
    const random = () => {
      if (window.crypto?.getRandomValues) {
        const value = new Uint32Array(1);
        window.crypto.getRandomValues(value);
        return value[0] / 4294967296;
      }
      return Math.random();
    };
    for (let index = result.length - 1; index > 0; index -= 1) {
      const pick = Math.floor(random() * (index + 1));
      [result[index], result[pick]] = [result[pick], result[index]];
    }
    return result;
  };

  // Select a different, product-diverse subset per visit, then chronological
  // sorting is applied to the chosen subset before it reaches the DOM.
  const selectDiverseReviews = (reviews, limit) => {
    if (reviews.length <= limit) return [...reviews];
    const groups = new Map();
    reviews.forEach((review) => {
      const key = review.product_handle || review.product_title || 'general';
      if (!groups.has(key)) groups.set(key, []);
      groups.get(key).push(review);
    });
    const buckets = shuffle(Array.from(groups.values()).map(shuffle));
    const selected = [];
    let round = 0;
    while (selected.length < limit && buckets.some((bucket) => bucket[round])) {
      for (const bucket of buckets) {
        if (bucket[round]) selected.push(bucket[round]);
        if (selected.length >= limit) break;
      }
      round += 1;
    }
    return selected;
  };

  const relativeTime = (review) => {
    const at = publishedAt(review);
    if (!at) return '';
    const diff = Math.max(0, Date.now() - at);
    const min = Math.floor(diff / 60000);
    if (min < 1) return 'Publicada ahora mismo';
    if (min < 60) return `Publicada hace ${min} ${min === 1 ? 'minuto' : 'minutos'}`;
    const hours = Math.floor(min / 60);
    if (hours < 24) return `Publicada hace ${hours} ${hours === 1 ? 'hora' : 'horas'}`;
    const days = Math.floor(hours / 24);
    if (days < 31) return `Publicada hace ${days} ${days === 1 ? 'día' : 'días'}`;
    const months = Math.floor(days / 30);
    if (months < 12) return `Publicada hace ${months} ${months === 1 ? 'mes' : 'meses'}`;
    const years = Math.floor(months / 12);
    return `Publicada hace ${years} ${years === 1 ? 'año' : 'años'}`;
  };

  const normalizedMedia = (review) => {
    if (Array.isArray(review.media_files) && review.media_files.length) {
      return { type: review.media_type || (review.media_files.length > 1 ? 'gallery' : 'image'), files: review.media_files };
    }
    if (review.image) {
      return { type: 'image', files: [{ file: review.image, alt: review.image_alt || 'Fotografía de la opinión' }] };
    }
    return { type: 'none', files: [] };
  };

  // On product pages, lead with real customer media and alternate it with
  // text-only opinions. This keeps proof visible early without turning the
  // review wall into a block of photos followed by a block of plain text.
  const prioritizeProductReviews = (reviews) => {
    const mediaReviews = reviews.filter(review => normalizedMedia(review).files.length);
    const textReviews = reviews.filter(review => !normalizedMedia(review).files.length);
    const ordered = [];
    const length = Math.max(mediaReviews.length, textReviews.length);
    for (let index = 0; index < length; index += 1) {
      if (mediaReviews[index]) ordered.push(mediaReviews[index]);
      if (textReviews[index]) ordered.push(textReviews[index]);
    }
    return ordered;
  };

  const reviewMedia = (review) => {
    const media = normalizedMedia(review);
    if (!media.files.length || media.type === 'none') return '';

    if (media.type === 'video') {
      const item = media.files[0];
      const source = resolveAsset(item.file);
      const poster = resolveAsset(item.poster);
      if (!source) return '';
      return `<figure class="lm-review-media lm-review-media--video">
        <video controls playsinline muted loop preload="metadata" data-review-autoplay${poster ? ` poster="${escapeHtml(poster)}"` : ''} aria-label="${escapeHtml(item.alt || `Vídeo de la opinión de ${review.customer_name}`)}">
          <source src="${escapeHtml(source)}" type="video/mp4">
          Tu navegador no puede reproducir este vídeo.
        </video>
      </figure>`;
    }

    const slides = media.files.map((item, index) => {
      const source = resolveAsset(item.file);
      if (!source) return '';
      return `<a class="lm-review-media__slide" href="${escapeHtml(source)}" target="_blank" rel="noopener" aria-label="Abrir imagen ${index + 1} de ${media.files.length} de la opinión de ${escapeHtml(review.customer_name)}">
        <img src="${escapeHtml(source)}" alt="${escapeHtml(item.alt || `Fotografía ${index + 1} de la opinión`)}" width="720" height="900" loading="lazy" decoding="async">
      </a>`;
    }).filter(Boolean).join('');
    if (!slides) return '';

    if (media.type === 'gallery' || media.files.length > 1) {
      return `<div class="lm-review-media lm-review-media--gallery" data-review-gallery>
        <div class="lm-review-media__track" data-review-gallery-track>${slides}</div>
        <div class="lm-review-media__controls">
          <button type="button" data-review-gallery-prev aria-label="Foto anterior">‹</button>
          <span><b data-review-gallery-current>1</b> / ${media.files.length}</span>
          <button type="button" data-review-gallery-next aria-label="Foto siguiente">›</button>
        </div>
      </div>`;
    }
    return `<div class="lm-review-media lm-review-media--image">${slides}</div>`;
  };

  const displayCustomerName = (value) => {
    const parts = String(value || '').trim().split(/\s+/).filter(Boolean);
    if (!parts.length) return 'Clienta';
    const first = parts[0];
    if (parts.length === 1) return first;
    const surnameInitial = parts[1].replace(/[^A-Za-zÀ-ÿ]/g, '').charAt(0).toUpperCase();
    return surnameInitial ? `${first} ${surnameInitial}.` : first;
  };

  const card = (review, layout, extra) => {
    const details = [review.size && `Talla ${review.size}`, review.color].filter(Boolean).join(' · ');
    const product = layout !== 'product' && review.product_title
      ? `<p class="lm-review-card__product">${review.product_handle
        ? `<a href="/products/${escapeHtml(review.product_handle)}">${escapeHtml(review.product_title)}</a>`
        : escapeHtml(review.product_title)}</p>` : '';
    const offer = review.offer
      ? `<span class="lm-review-card__offer">${escapeHtml(review.offer)}</span>` : '';
    const media = reviewMedia(review);
    return `<article class="lm-review-card"${extra ? ' data-review-extra hidden' : ''}>
      <div class="lm-review-card__top">${stars(review.rating)}<time datetime="${escapeHtml(review.date)}">${escapeHtml(formatDate(review.date))}</time></div>
      <h3>${escapeHtml(review.title)}</h3>
      <p class="lm-review-card__body">${escapeHtml(review.review)}</p>
      ${product}${offer}
      <footer>
        <strong>${escapeHtml(displayCustomerName(review.customer_name))}</strong>
                ${review.verified ? '<span class="lm-review-card__verified">✓ Compra verificada</span>' : ''}
        ${details ? `<span class="lm-review-card__details">${escapeHtml(details)}</span>` : ''}
      </footer>
      ${media}
    </article>`;
  };

  /* Tarjeta compacta del muro de opiniones: estrellas verdes, frase, autoría y
   * antigüedad relativa. Altura natural, sin scroll interno. */
  const compactCard = (review) => `<article class="lm-review-card lm-review-card--compact">
    <div class="lm-review-card__compact-top"><span class="af-stars" aria-label="${escapeHtml(review.rating)} de 5 estrellas">★★★★★</span><span class="lm-review-card__time">${escapeHtml(relativeTime(review))}</span></div>
    ${review.title ? `<h3>${escapeHtml(review.title)}</h3>` : ''}
    <p class="lm-review-card__text">${escapeHtml(review.review || review.title)}</p>
    <footer class="lm-review-card__compact-footer"><strong>${escapeHtml(displayCustomerName(review.customer_name))}</strong>${review.product_title ? `<span>${escapeHtml(review.product_title)}</span>` : ''}</footer>
  </article>`;

  /* Tarjeta de perfil para el carrusel horizontal de la PDP. */
  const profileCard = (review) => {
    const name = displayCustomerName(review.customer_name);
    const initials = name.split(/\s+/).map(part => part.charAt(0)).join('').slice(0, 2).toUpperCase();
    const media = normalizedMedia(review);
    const photo = review.avatar
      ? resolveAsset(review.avatar)
      : (media.files.length && media.type !== 'video' ? resolveAsset(media.files[0].file) : '');
    const avatar = photo
      ? `<img class="lm-review-profile__avatar" src="${escapeHtml(photo)}" alt="" width="42" height="42" loading="lazy" decoding="async">`
      : `<span class="lm-review-profile__avatar lm-review-profile__avatar--initials" aria-hidden="true">${escapeHtml(initials)}</span>`;
    const details = [review.size && `Talla ${review.size}`, review.color].filter(Boolean).join(' · ');
    return `<li class="lm-review-strip__item">
      <article class="lm-review-profile">
        ${avatar}
        <div>
          <div class="lm-review-profile__head">
            <p class="lm-review-profile__name">${escapeHtml(name)}</p>
            <span class="af-stars" aria-label="${escapeHtml(review.rating)} de 5 estrellas">★★★★★</span>
          </div>
          <p class="lm-review-profile__text">“${escapeHtml(review.review)}”</p>
          ${details ? `<p class="lm-review-profile__meta">${escapeHtml(details)}</p>` : ''}
          ${review.demo ? '<span class="lm-review-profile__demo">Perfil anónimo</span>' : ''}
        </div>
      </article>
    </li>`;
  };

  /* Auto-scroll muy lento y continuo, que se pausa en cuanto la usuaria toca,
   * arrastra o enfoca el carrusel, y que nunca captura el gesto vertical. */
  const initReviewStrip = (track) => {
    if (track.dataset.autoscroll === 'on') return;
    track.dataset.autoscroll = 'on';
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    let paused = false;
    let raf = null;
    let last = 0;
    const SPEED = 14;                                     // px por segundo

    const step = (now) => {
      if (!last) last = now;
      const dt = (now - last) / 1000;
      last = now;
      if (!paused) {
        const max = track.scrollWidth - track.clientWidth;
        if (max > 1) {
          const next = track.scrollLeft + SPEED * dt;
          track.scrollLeft = next >= max - 0.5 ? 0 : next;
        }
      }
      raf = window.requestAnimationFrame(step);
    };

    const play = () => { if (!raf) { last = 0; raf = window.requestAnimationFrame(step); } };
    const stop = () => { if (raf) { window.cancelAnimationFrame(raf); raf = null; } };

    ['pointerdown', 'touchstart', 'mouseenter', 'focusin', 'wheel'].forEach((evt) =>
      track.addEventListener(evt, () => { paused = true; }, { passive: true }));
    ['pointerup', 'touchend', 'mouseleave', 'focusout'].forEach((evt) =>
      track.addEventListener(evt, () => { window.setTimeout(() => { paused = false; }, 2500); }, { passive: true }));

    // Sólo se mueve mientras está en pantalla.
    if ('IntersectionObserver' in window) {
      new IntersectionObserver((entries) => (entries[0].isIntersecting ? play() : stop()), { rootMargin: '80px' }).observe(track);
    } else {
      play();
    }
    document.addEventListener('visibilitychange', () => (document.hidden ? stop() : play()));
  };

  const initGallery = (gallery) => {
    const track = gallery.querySelector('[data-review-gallery-track]');
    const current = gallery.querySelector('[data-review-gallery-current]');
    if (!track) return;
    const move = (direction) => track.scrollBy({ left: direction * track.clientWidth, behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' });
    gallery.querySelector('[data-review-gallery-prev]')?.addEventListener('click', () => move(-1));
    gallery.querySelector('[data-review-gallery-next]')?.addEventListener('click', () => move(1));
    track.addEventListener('scroll', () => {
      if (current) current.textContent = String(Math.round(track.scrollLeft / Math.max(1, track.clientWidth)) + 1);
    }, { passive: true });
  };

  const initAutoplayVideo = (video) => {
    // Reviews stay lightweight while scrolling; video starts only on user action.
    video.pause();
    video.removeAttribute('autoplay');
  };

  const updateSummary = (root, aggregate) => {
    root.querySelectorAll('[data-review-average]').forEach(element => { element.textContent = aggregate.average.toFixed(1).replace('.', ','); });
    root.querySelectorAll('[data-review-count]').forEach(element => { element.textContent = `${aggregate.count} ${aggregate.count === 1 ? 'opinión' : 'opiniones'}`; });
    root.querySelectorAll('[data-review-stars]').forEach(element => { element.style.setProperty('--rating', `${aggregate.average * 20}%`); });
  };

  class ClaraReviews extends HTMLElement {
    connectedCallback() { this.render(); }

    async render() {
      try {
        const allReviews = await loadReviews(this.dataset.reviewsSource);
        const reviews = this.dataset.scope === 'all'
          ? allReviews
          : this.dataset.scope === 'home'
            ? allReviews.filter(review => review.scope === 'home')
            : allReviews.filter(review => matchesProduct(review, this.dataset.productHandle, this.dataset.productTitle));

        const aggregate = summary(reviews);
        const list = this.querySelector('[data-review-list]');
        const limit = Math.max(1, Number(this.dataset.initialLimit || reviews.length));
        const layout = this.dataset.layout || 'product';
        const rotatingLayout = layout === 'home' || layout === 'wall';
        const chosenReviews = rotatingLayout ? selectDiverseReviews(reviews, limit) : reviews;
        const orderedReviews = newestFirst(chosenReviews);
        const visibleReviews = rotatingLayout ? orderedReviews.slice(0, limit) : orderedReviews;
        updateSummary(this, aggregate);
        if (list) {
          if (layout === 'strip') {
            // Carrusel horizontal de perfiles. Se duplica la tanda cuando hay
            // pocas reseñas para que el bucle no dé tirones al reiniciarse.
            const source = orderedReviews.length && orderedReviews.length < 6
              ? [...orderedReviews, ...orderedReviews]
              : orderedReviews;
            list.innerHTML = source.map(profileCard).join('');
            initReviewStrip(list);
          } else if (layout === 'wall') {
            list.innerHTML = visibleReviews.map(compactCard).join('');
          } else {
            list.innerHTML = visibleReviews.map((review, index) => card(review, layout, layout === 'product' && index >= limit)).join('');
            list.querySelectorAll('[data-review-gallery]').forEach(initGallery);
            list.querySelectorAll('video[data-review-autoplay]').forEach(initAutoplayVideo);
          }
        }

        const empty = this.querySelector('[data-review-empty]');
        const aggregateBlock = this.querySelector('[data-review-aggregate]');
        if (empty) empty.hidden = reviews.length > 0;
        if (aggregateBlock) aggregateBlock.hidden = reviews.length === 0;

        const distribution = this.querySelector('[data-review-distribution]');
        if (distribution) {
          distribution.hidden = reviews.length === 0;
          distribution.innerHTML = aggregate.distribution.map(item => {
            const percent = aggregate.count ? Math.round(item.count * 100 / aggregate.count) : 0;
            return `<div class="lm-reviews__distribution-row">
              <span>${item.rating} ★</span><span class="lm-reviews__bar"><i style="width:${percent}%"></i></span><span>${item.count}</span>
            </div>`;
          }).join('');
        }

        const more = this.querySelector('[data-review-more]');
        if (more && reviews.length > limit) {
          more.hidden = false;
          more.addEventListener('click', () => {
            const extras = this.querySelectorAll('[data-review-extra]');
            const expanding = Array.from(extras).some(element => element.hidden);
            extras.forEach(element => { element.hidden = !expanding; });
            more.textContent = expanding ? 'Ver menos opiniones' : 'Ver más opiniones';
            more.setAttribute('aria-expanded', String(expanding));
          });
        }
        this.hidden = false;
      } catch (error) {
        this.hidden = true;
      }
    }
  }

  if (!customElements.get('lm-reviews')) customElements.define('lm-reviews', ClaraReviews);

  const initSummaries = async () => {
    document.querySelectorAll('[data-review-summary]').forEach(async link => {
      try {
        const reviews = (await loadReviews(link.dataset.reviewsSource))
          .filter(review => matchesProduct(review, link.dataset.productHandle, link.dataset.productTitle));
        const aggregate = summary(reviews);
        updateSummary(link, aggregate);
        if (reviews.length) {
          link.setAttribute('aria-label', `${aggregate.average.toFixed(1).replace('.', ',')} de 5; ${aggregate.count} opiniones. Ver opiniones.`);
        } else {
          link.querySelectorAll('[data-review-average], [data-review-count]').forEach(element => { element.hidden = true; });
          const emptyLabel = link.querySelector('[data-review-empty-label]');
          if (emptyLabel) emptyLabel.hidden = false;
          link.setAttribute('aria-label', 'Escribir la primera opinión del producto.');
        }
        link.hidden = false;
        link.addEventListener('click', event => {
          const candidates = Array.from(document.querySelectorAll('#product-reviews, #product-reviews-mobile'));
          const target = candidates.find(element => element.offsetParent !== null);
          if (!target) return;
          event.preventDefault();
          if (!reviews.length) {
            const form = target.querySelector('.lm-review-form');
            if (form) form.open = true;
          }
          target.scrollIntoView({ behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth', block: 'start' });
          history.replaceState(null, '', `#${target.id}`);
        });
      } catch {}
    });
  };

  const initReviewExtras = async () => {
    document.querySelectorAll('[data-card-review-mini]').forEach(async element => {
      try {
        const reviews = (await loadReviews(element.dataset.reviewsSource))
          .filter(review => matchesProduct(review, element.dataset.productHandle, ''));
        if (!reviews.length) return;
        const aggregate = summary(reviews);
        const score = element.querySelector('[data-mini-review-score]');
        if (score) score.textContent = `${aggregate.average.toFixed(1).replace('.', ',')}/5`;
        element.hidden = false;
      } catch {}
    });

    document.querySelectorAll('[data-featured-review]').forEach(async element => {
      try {
        const reviews = (await loadReviews(element.dataset.reviewsSource))
          .filter(review => matchesProduct(review, element.dataset.productHandle, element.dataset.productTitle));
        if (!reviews.length) return;
        const review = newestFirst(reviews)[0];
        const avatar = element.querySelector('[data-featured-review-avatar]');
        const name = element.querySelector('[data-featured-review-name]');
        const body = element.querySelector('[data-featured-review-text]');
        const meta = element.querySelector('[data-featured-review-meta]');
        const starsNode = element.querySelector('[data-featured-review-stars]');
        if (avatar) {
          const avatarSource = review.avatar ? resolveAsset(review.avatar) : '';
          avatar.innerHTML = avatarSource
            ? `<img src="${escapeHtml(avatarSource)}" alt="" width="52" height="52" loading="lazy" decoding="async">`
            : `<span aria-hidden="true">${escapeHtml(String(review.customer_name || '?').trim().slice(0, 1).toUpperCase())}</span>`;
        }
        if (name) name.textContent = review.customer_name || 'Clienta';
        if (body) body.textContent = review.review || '';
        if (starsNode) starsNode.style.setProperty('--rating', `${Math.max(0, Math.min(5, Number(review.rating || 0))) * 20}%`);
        if (meta) {
          const parts = [review.size && `Talla ${review.size}`, review.color, review.verified ? 'Compra verificada' : 'Opinión publicada'].filter(Boolean);
          meta.textContent = parts.join(' · ');
        }
        element.hidden = false;
      } catch {}
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => { initSummaries(); initReviewExtras(); }, { once: true });
  } else {
    initSummaries();
    initReviewExtras();
  }
})();
